import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postData } from '../backend';
import { setAuthToken, setRefreshToken } from '../auth';
import SalesforceRecordPanel from './AppSalesforceEmbedRecordPanel';

// Rendered inside a Salesforce Canvas iframe on a record page.
// The backend verifies the Canvas signed request and redirects here with a short lived auto-login state.

function AppSalesforceEmbed() {
  const { t } = useTranslation();

  const [params] = useState(() => Object.fromEntries(new URLSearchParams(window.location.search).entries()));
  const { state, objectName, recordId, error: canvasError } = params;

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);

  useEffect(() => {
    if (!state)
      return;

    postData("/auth/auto-login/", { state })
      .then(({ token, refreshToken }) => {
        setAuthToken(token);
        setRefreshToken(refreshToken);
        setIsLoggedIn(true);

        // The state is short lived, Salesforce posts a new one each time the record page is loaded
        const url = new URL(window.location.href);
        url.searchParams.delete("state");
        window.history.replaceState(null, "", url.toString());
      })
      .catch(() => setLoginFailed(true));
  }, [state]);

  let content: JSX.Element;

  if (canvasError === "NOT_CONNECTED")
    content = (
      <div className="text-sm text-gray-500">
        {t("Connect your Salesforce account in Portant to run workflows from this record.")}{" "}
        <a className="text-blue hover:underline" href={`${process.env.PUBLIC_URL}/`} target="_blank" rel="noreferrer">{t("Open Portant")}</a>
      </div>
    );
  else if (canvasError || !objectName || !recordId)
    content = <div className="text-sm text-gray-500">{t("Portant can only be used from a Salesforce record page.")}</div>;
  else if (loginFailed || (!state && !isLoggedIn))
    content = <div className="text-sm text-gray-500">{t("Your session has expired, please reload the page.")}</div>;
  else if (!isLoggedIn)
    content = <img src="https://static.portant.co/portant-loading-gray.svg" className="w-8 h-8" alt="Loading" />;
  else
    content = <SalesforceRecordPanel objectName={objectName} recordId={recordId} />;

  return (
    <div className="flex flex-col w-screen min-h-screen gap-3 p-3 bg-white">
      {content}
      <div className="flex items-center gap-1 mt-auto text-xs font-semibold text-gray-600 font-gilroy">
        <img className="w-5 h-5" src="https://static.portant.co/logo-mark.svg" alt="Portant Logo" />
        {t("Powered by")} <a href="https://www.portant.co" target="_blank" rel="noreferrer" className="text-black hover:underline hover:text-blue">Portant</a>
      </div>
    </div>
  );
}

export default AppSalesforceEmbed;
